/**
 * 21d: case document upload (`POST /cases/{caseId}/documents`, raw body, metadata in the query). The route is built
 * by the api-client from the route descriptor; the bytes go with `fetch` because the client's JSON transport cannot carry
 * a file. Used by the case page (session cookie) and the external "Your items" page (invitation token header).
 */
import { type ApiClient, errorFromResponse } from '@agentic/api-client';
import { Document, EvidenceExport, PlatformError, wave21OperatorRoutes } from '@agentic/contracts';
import { client } from '../../app/api.ts';
import { attachmentContentType } from './start/attachments.tsx';
import type { EvidenceDownload, UploadDocumentMeta } from './wave21-panels.tsx';

export async function uploadCaseDocument(
  api: Pick<ApiClient, 'url' | 'options'>,
  caseId: string,
  file: File,
  meta: UploadDocumentMeta,
  fetchImpl: typeof fetch = globalThis.fetch.bind(globalThis),
): Promise<Document> {
  const url = api.url(wave21OperatorRoutes.uploadDocument, {
    params: { caseId },
    query: { ...meta, filename: file.name },
  });
  let res: Response;
  try {
    res = await fetchImpl(url, {
      method: 'POST',
      headers: {
        ...headersOf(api.options.headers),
        'content-type': attachmentContentType(file),
        accept: 'application/json',
      },
      body: file,
      credentials: api.options.credentials ?? 'include',
      // The external page uploads with an invitation token: never send a Referer.
      referrerPolicy: 'no-referrer',
    });
  } catch (cause) {
    throw new PlatformError('downstream_failed', 'Network error calling cases.documents.upload', { cause });
  }
  if (!res.ok) throw await errorFromResponse(res, 'cases.documents.upload');
  return Document.parse(await res.json());
}

function headersOf(h: ApiClient['options']['headers']): Record<string, string> {
  return typeof h === 'function' ? h() : { ...(h ?? {}) };
}

/**
 * `GET /cases/{caseId}/evidence-index` read as bytes: the download saves exactly what the server sent (its checksum is
 * over the canonical JSON of the export's fields); the parsed copy only feeds the page.
 */
export async function downloadEvidence(
  caseId: string,
  api: Pick<ApiClient, 'url' | 'options'> = client,
  fetchImpl: typeof fetch = globalThis.fetch.bind(globalThis),
): Promise<EvidenceDownload> {
  let res: Response;
  try {
    res = await fetchImpl(api.url(wave21OperatorRoutes.evidenceIndex, { params: { caseId } }), {
      headers: { ...headersOf(api.options.headers), accept: 'application/json' },
      credentials: api.options.credentials ?? 'include',
    });
  } catch (cause) {
    throw new PlatformError('downstream_failed', 'Network error calling cases.evidenceIndex', { cause });
  }
  if (!res.ok) throw await errorFromResponse(res, 'cases.evidenceIndex');
  const bytes = await res.arrayBuffer();
  const exported = EvidenceExport.parse(JSON.parse(new TextDecoder().decode(bytes)));
  return { raw: new Blob([bytes], { type: 'application/json' }), exported };
}
